import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Divider,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {t} from 'i18next';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrModal, {ModalButton} from '../../../common/arr-modal/ArrModal';
import ArrMenu, {ArrMenuHandle} from '../../../common/arr-menu/ArrMenu';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {ReactComponent as ExpandCollapseIcon} from '../../../../assets/images/expand-collapse-icon.svg';
import {ReactComponent as SearchIcon} from '../../../../assets/images/icon-search.svg';
import {ReactComponent as PlusIcon} from '../../../../assets/images/plus-icon.svg';
import {ReactComponent as CloseIcon} from '../../../../assets/images/close-base-icon.svg';
import {ReactComponent as MoreIcon} from '../../../../assets/images/menu.svg';
import Search from '@mui/icons-material/Search';
import Cancel from '@mui/icons-material/Cancel';
import GroupNameComponent from '../common/GroupNameComponent/GroupNameComponent';
import {ToastType} from '../../../../shared/constants';
import {IAIReviewJobGroupsResponse} from '../../../../api/types/aiReview.type';
import {
  useCreateReviewJobGroup,
  useDeleteReviewJobGroup,
  useUpdateReviewJobGroup,
} from '../../../../hooks/ai/review/useReview';
import './ReviewLeftContentPanel.scss';
import {IJobGroup} from '../utils';

interface ReviewLeftContentPanelProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  reviewJobGroupsResponse?: IAIReviewJobGroupsResponse;
  selectedGroup: IJobGroup | null;
  setSelectedGroup: React.Dispatch<React.SetStateAction<IJobGroup | null>>;
  allJobsCount?: number;
  hasGroupLoadError?: boolean;
  onRetryLoadGroups?: () => void;
  isGroupListPending?: boolean;
}

const ReviewLeftContentPanel: React.FC<ReviewLeftContentPanelProps> = ({
  isCollapsed,
  onToggleCollapse,
  reviewJobGroupsResponse,
  selectedGroup,
  setSelectedGroup,
  allJobsCount = 0,
  hasGroupLoadError = false,
  onRetryLoadGroups,
  isGroupListPending = false,
}) => {
  const MAX_GROUP_NAME_LENGTH = 40;
  const [isGroupSectionVisible, setIsGroupSectionVisible] =
    useState<boolean>(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState<boolean>(false);
  const [isSearchingGroups, setIsSearchingGroups] = useState<boolean>(false);
  const [groupName, setGroupName] = useState<string>('');
  const [groupSearchValue, setGroupSearchValue] = useState<string>('');
  const [appliedGroupSearch, setAppliedGroupSearch] = useState<string>('');
  const [groupSearchPerformed, setGroupSearchPerformed] =
    useState<boolean>(false);
  const [isGroupMenuOpen, setIsGroupMenuOpen] = useState<boolean>(false);
  const [activeGroupNameForMenu, setActiveGroupNameForMenu] = useState<
    string | null
  >(null);
  const groupMenuRef = useRef<ArrMenuHandle | null>(null);
  const ignoreGroupNameCommitRef = useRef(false);
  const [isEditingGroup, setIsEditingGroup] = useState<boolean>(false);
  const [editingGroupOriginalName, setEditingGroupOriginalName] = useState<
    string | null
  >(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [pendingDeleteGroupName, setPendingDeleteGroupName] = useState<
    string | null
  >(null);
  const [showGroupToast, setShowGroupToast] = useState<boolean>(false);
  const [groupToastStatus, setGroupToastStatus] = useState<ToastType>(
    ToastType.Success
  );
  const [groupToastMessage, setGroupToastMessage] = useState<string>('');
  const openGroupToast = useCallback((status: ToastType, message: string) => {
    setGroupToastStatus(status);
    setGroupToastMessage(message);
    setShowGroupToast(true);
  }, []);

  const {mutate: createReviewJobGroup} = useCreateReviewJobGroup();
  const {mutate: updateReviewJobGroup} = useUpdateReviewJobGroup();
  const {mutate: deleteReviewJobGroup} = useDeleteReviewJobGroup();

  const invalidCharRegex = useMemo(() => /[<>*;'/()#&]/, []);
  const hasSpecialCharError = invalidCharRegex.test(groupName);
  const trimmedGroupName = groupName.trim();

  const apiGroups =
    reviewJobGroupsResponse?.jobGroups?.map((jg) => ({
      name: jg.name,
      jobGroupId: jg.jobGroupId,
      count: jg.jobCount,
      isSelected: false,
    })) ?? [];

  const trimmedGroupNameLower = trimmedGroupName.toLowerCase();

  const hasDuplicateError =
    trimmedGroupName.length > 0 &&
    apiGroups.some((g) => {
      const currentNameLower = g.name.toLowerCase();
      if (
        isEditingGroup &&
        editingGroupOriginalName &&
        currentNameLower === editingGroupOriginalName.toLowerCase()
      ) {
        return false;
      }
      return currentNameLower === trimmedGroupNameLower;
    });

  const hasInlineError = hasSpecialCharError || hasDuplicateError;

  const hasAnyGroups = apiGroups.length > 0;

  const showNoGroupsBox =
    !isGroupSectionVisible && apiGroups.length === 0 && !isGroupListPending;
  const showGroupLoadErrorBox =
    hasGroupLoadError && !isGroupSectionVisible && apiGroups.length === 0;

  useEffect(() => {
    if (
      apiGroups.length === 0 &&
      !isCreatingGroup &&
      !isSearchingGroups &&
      !isEditingGroup
    ) {
      setIsGroupSectionVisible(false);
    }
  }, [apiGroups.length, isCreatingGroup, isSearchingGroups, isEditingGroup]);

  const openGroupCreation = () => {
    ignoreGroupNameCommitRef.current = false;
    setIsGroupSectionVisible(true);
    setIsCreatingGroup(true);
    setIsSearchingGroups(false);
    setGroupSearchValue('');
    setAppliedGroupSearch('');
    setGroupSearchPerformed(false);
  };

  const handleGroupsSearchToggle = () => {
    if (isSearchingGroups) {
      setIsSearchingGroups(false);
      setGroupSearchValue('');
      setAppliedGroupSearch('');
      setGroupSearchPerformed(false);
      return;
    }
    setGroupName('');
    setIsGroupSectionVisible(true);
    setIsSearchingGroups(true);
    setIsCreatingGroup(false);
  };

  const handleCollapseToggle = () => {
    onToggleCollapse();
    requestAnimationFrame(() => {
      const collapseButton = document.querySelector<HTMLElement>(
        '[data-testid="review-left-panel-expand-collapse"]'
      );
      collapseButton?.focus({preventScroll: true});
    });
  };

  const handleGroupNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value.slice(0, MAX_GROUP_NAME_LENGTH);
    setGroupName(next);
  };

  const handleGroupSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setGroupSearchValue(e.target.value);
  };

  const handleGroupSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const trimmed = groupSearchValue.trim();
      setAppliedGroupSearch(trimmed);
      setGroupSearchPerformed(trimmed.length > 0);
    }
  };

  const handleGroupSearchIconClick = () => {
    const trimmed = groupSearchValue.trim();
    if (!trimmed) return;
    setAppliedGroupSearch(trimmed);
    setGroupSearchPerformed(true);
  };

  const handleGroupSearchClear = () => {
    setGroupSearchValue('');
    setAppliedGroupSearch('');
    setGroupSearchPerformed(false);
  };

  const handleGroupSearchBlur = () => {
    if (!groupSearchValue.trim()) {
      setIsSearchingGroups(false);
      setAppliedGroupSearch('');
      setGroupSearchPerformed(false);
      setGroupSearchValue('');
    }
  };

  const handleEditBlur = (e: React.FocusEvent) => {
    const currentTarget = e.currentTarget;
    requestAnimationFrame(() => {
      if (!currentTarget.contains(document.activeElement)) {
        finishEditingGroup();
      }
    });
  };

  const groupMenuOptions = useMemo(
    () => [
      {
        id: 'rename',
        label: t('ai.review.leftPanel.renameGroup'),
        variant: 'heading' as const,
      },
      {
        id: 'delete',
        label: t('ai.review.leftPanel.deleteGroup'),
        variant: 'danger' as const,
      },
    ],
    [t]
  );

  const handleGroupMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    groupName: string
  ) => {
    setActiveGroupNameForMenu(groupName);
    groupMenuRef.current?.open(event.currentTarget);
    setIsGroupMenuOpen(true);
  };

  const handleGroupMenuItemClick = (item: {id: string}) => {
    if (item?.id === 'delete' && activeGroupNameForMenu) {
      setPendingDeleteGroupName(activeGroupNameForMenu);
      setIsDeleteModalOpen(true);
    }

    if (item?.id === 'rename' && activeGroupNameForMenu) {
      setIsGroupMenuOpen(false);
      setTimeout(() => {
        ignoreGroupNameCommitRef.current = false;
        setIsCreatingGroup(false);
        setIsSearchingGroups(false);
        setIsEditingGroup(true);
        setEditingGroupOriginalName(activeGroupNameForMenu);
        setGroupName(activeGroupNameForMenu);
      }, 0);
    } else {
      setIsGroupMenuOpen(false);
    }
  };

  const handleGroupMenuClose = () => {
    setIsGroupMenuOpen(false);
    setActiveGroupNameForMenu(null);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setPendingDeleteGroupName(null);
  };

  const handleConfirmDeleteGroup = () => {
    if (pendingDeleteGroupName) {
      const group = apiGroups.find((g) => g.name === pendingDeleteGroupName);
      if (group) {
        deleteReviewJobGroup(
          {jobGroupId: group.jobGroupId},
          {
            onSuccess: (result) => {
              if (result.errorMessage !== '') {
                openGroupToast(ToastType.Error, result.errorMessage);
              } else {
                openGroupToast(
                  ToastType.Success,
                  t('ai.review.leftPanel.groupDeletedSuccess', {
                    groupName: pendingDeleteGroupName,
                  })
                );
                setSelectedGroup((current) =>
                  current?.name === pendingDeleteGroupName ? null : current
                );
              }
            },
            onError: () => {
              openGroupToast(
                ToastType.Error,
                t('ai.review.leftPanel.groupDeletedFailure', {
                  groupName: pendingDeleteGroupName,
                })
              );
            },
          }
        );
      }
    }
    setIsDeleteModalOpen(false);
    setPendingDeleteGroupName(null);
  };

  const createGroupFromInput = () => {
    if (ignoreGroupNameCommitRef.current) return;
    if (hasInlineError) return;

    const name = groupName.trim();
    if (name.length === 0) {
      setIsCreatingGroup(false);
      setIsGroupSectionVisible(true);
      return;
    }
    createReviewJobGroup(
      {name},
      {
        onSuccess: () => {
          setGroupName('');
          setIsCreatingGroup(false);
          setIsGroupSectionVisible(true);
          openGroupToast(
            ToastType.Success,
            t('ai.review.leftPanel.groupCreatedSuccess', {groupName: name})
          );
        },
        onError: () => {
          openGroupToast(
            ToastType.Error,
            t('ai.review.leftPanel.groupCreatedFailure', {groupName: name})
          );
        },
      }
    );
  };

  const finishEditingGroup = () => {
    if (ignoreGroupNameCommitRef.current) return;
    const trimmed = groupName.trim();
    if (!trimmed) {
      setIsEditingGroup(false);
      setEditingGroupOriginalName(null);
      setGroupName('');
      return;
    }
    if (
      editingGroupOriginalName &&
      trimmed.toLowerCase() === editingGroupOriginalName.trim().toLowerCase()
    ) {
      setIsEditingGroup(false);
      setEditingGroupOriginalName(null);
      setGroupName('');
      return;
    }
    if (hasInlineError || !editingGroupOriginalName) {
      return;
    }

    const group = apiGroups.find((g) => g.name === editingGroupOriginalName);
    if (!group) {
      return;
    }

    updateReviewJobGroup(
      {jobGroupId: group.jobGroupId, name: trimmed},
      {
        onSuccess: () => {
          setSelectedGroup((currentSelected) =>
            currentSelected?.name === editingGroupOriginalName
              ? {...currentSelected, name: trimmed}
              : currentSelected
          );

          setIsEditingGroup(false);
          setEditingGroupOriginalName(null);
          setGroupName('');
          openGroupToast(
            ToastType.Success,
            t('ai.review.leftPanel.groupEditedSuccess', {
              previousName: editingGroupOriginalName,
              newName: trimmed,
            })
          );
        },
        onError: () => {
          openGroupToast(
            ToastType.Error,
            t('ai.review.leftPanel.groupEditedFailure', {newName: trimmed})
          );
        },
      }
    );
  };

  const handleGroupNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isEditingGroup) {
        finishEditingGroup();
      } else {
        createGroupFromInput();
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      ignoreGroupNameCommitRef.current = true;
      setIsCreatingGroup(false);
      setIsEditingGroup(false);
      setEditingGroupOriginalName(null);
      setGroupName('');
    }
  };

  const sortedGroups = useMemo(
    () =>
      apiGroups.slice().sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          sensitivity: 'base',
        })
      ),
    [apiGroups]
  );

  const visibleGroups = useMemo(() => {
    const query = appliedGroupSearch.trim().toLowerCase();
    if (!query) {
      return sortedGroups;
    }

    return sortedGroups.filter((g) => g.name.toLowerCase().includes(query));
  }, [sortedGroups, appliedGroupSearch]);

  return (
    <Box
      className="review-left-content-panel"
      data-testid="review-left-content-panel"
      aria-busy={isGroupListPending}
    >
      {!isCollapsed ? (
        <>
          <Box
            className="review-left-panel-item-wrapper"
            data-testid="review-left-panel-item-wrapper"
          >
            <GroupNameComponent
              name={t('ai.review.leftPanel.allJobs')}
              count={allJobsCount}
              isSelected={selectedGroup === null}
              dataTestId="all-jobs-box"
              onClick={() => setSelectedGroup(null)}
            />
            <ArrIconButton
              icon={<ExpandCollapseIcon />}
              ariaLabel={t('common.collapse')}
              size="small"
              className="expand-collapse-button"
              testId="review-left-panel-expand-collapse"
              onClick={handleCollapseToggle}
            />
            <Divider className="item-divider" />
          </Box>
          {showGroupLoadErrorBox && (
            <Box
              className="no-groups-box groups-load-error-box"
              data-testid="groups-load-error-box"
            >
              <Box
                className="no-groups-content groups-load-error-content"
                data-testid="groups-load-error-content"
              >
                <Typography
                  className="typo-text subhead4_size text-headings groups-load-error-title"
                  data-testid="groups-load-error-title"
                >
                  {t('ai.review.leftPanel.groupApiFailureTitle')}
                </Typography>
                <Typography
                  className="typo-text body3_size text-body-subtle groups-load-error-description"
                  data-testid="groups-load-error-description"
                >
                  {t('ai.review.leftPanel.groupApiFailureDescription')}
                </Typography>
                <ArrButton
                  variant="primary"
                  size="small"
                  disabled={false}
                  aria-label={t('common.refresh')}
                  dataTestId="groups-load-error-refresh-button"
                  onClick={onRetryLoadGroups}
                >
                  {t('common.refresh')}
                </ArrButton>
              </Box>
            </Box>
          )}
          {showNoGroupsBox && !showGroupLoadErrorBox && (
            <Box className="no-groups-box" data-testid="no-groups-box">
              <Box
                className="no-groups-content"
                data-testid="no-groups-content"
              >
                <Typography
                  className="typo-text subhead4_size no-groups-title"
                  data-testid="no-groups-title"
                >
                  {t('ai.review.leftPanel.emptyLeftPanelContent.title')}
                </Typography>
                <Typography
                  className="typo-text body3_size no-groups-description"
                  data-testid="no-groups-description"
                >
                  {t('ai.review.leftPanel.emptyLeftPanelContent.description')}
                </Typography>
                <ArrButton
                  variant="secondary"
                  size="medium"
                  disabled={false}
                  aria-label={t('ai.review.leftPanel.createAGroupBtn')}
                  dataTestId="create-group-button"
                  onClick={openGroupCreation}
                >
                  {t('ai.review.leftPanel.createAGroupBtn')}
                </ArrButton>
              </Box>
            </Box>
          )}

          {(isGroupSectionVisible || hasAnyGroups) && (
            <Box className="groups-section" data-testid="groups-section">
              <Box className="groups-header-row">
                <Typography
                  className="typo-text label2_size groups-title"
                  data-testid={`review-left-content-panel-groups`}
                >
                  {t('ai.review.leftPanel.groups')}
                </Typography>
                <Box className="groups-actions">
                  <ArrIconButton
                    icon={isSearchingGroups ? <CloseIcon /> : <SearchIcon />}
                    ariaLabel={
                      isSearchingGroups
                        ? t('ai.review.leftPanel.closeSearchBox')
                        : t('ai.review.leftPanel.searchGroupName')
                    }
                    size="medium"
                    testId="review-left-panel-groups-search"
                    className="groups-search-button"
                    onClick={handleGroupsSearchToggle}
                  />
                  <ArrIconButton
                    icon={<PlusIcon />}
                    ariaLabel={t('ai.review.leftPanel.createGroupName')}
                    size="medium"
                    testId="review-left-panel-groups-add"
                    className="groups-add-button"
                    disabled={isCreatingGroup}
                    onClick={openGroupCreation}
                  />
                </Box>
              </Box>

              {(isCreatingGroup || isSearchingGroups) && (
                <Box
                  className="create-group-input-wrapper"
                  data-testid="create-group-input-wrapper"
                >
                  {isCreatingGroup && (
                    <>
                      <ArrTextField
                        placeholder={t(
                          'ai.review.leftPanel.enterGroupPlaceholder'
                        )}
                        value={groupName}
                        onChange={handleGroupNameChange}
                        onKeyDown={handleGroupNameKeyDown}
                        onBlur={createGroupFromInput}
                        state={hasInlineError ? 'error' : 'enabled'}
                        showErrorIcon={hasInlineError}
                        sizeVariant="small"
                        autoFocus
                        dataTestId="enter-group-name-input"
                      />
                      {hasInlineError ? (
                        <Typography
                          className="typo-text body4_size group-name-error"
                          data-testid={`review-left-content-panel-${hasSpecialCharError ? 'sc-error' : 'dj-error'}`}
                        >
                          {hasSpecialCharError
                            ? t('ai.review.leftPanel.specialCharacterError')
                            : t('ai.review.leftPanel.duplicateJobGroupError')}
                        </Typography>
                      ) : (
                        <Typography
                          className="typo-text body4_size group-name-counter"
                          data-testid={`review-left-content-panel-of-count`}
                        >
                          {groupName.length} of {MAX_GROUP_NAME_LENGTH}
                        </Typography>
                      )}
                    </>
                  )}

                  {isSearchingGroups && (
                    <ArrTextField
                      value={groupSearchValue}
                      onChange={handleGroupSearchChange}
                      onKeyDown={handleGroupSearchKeyDown}
                      onBlur={handleGroupSearchBlur}
                      sizeVariant="small"
                      autoFocus
                      dataTestId="group-search-input"
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              {groupSearchValue && groupSearchPerformed ? (
                                <IconButton
                                  onClick={handleGroupSearchClear}
                                  aria-label={t('common.clear')}
                                  edge="end"
                                  size="small"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      handleGroupSearchClear();
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }
                                  }}
                                  className="clear-icon-btn"
                                  data-testid="group-search-clear-button"
                                >
                                  <Cancel
                                    className="clear-btn"
                                    fontSize="small"
                                  />
                                </IconButton>
                              ) : (
                                <IconButton
                                  onClick={handleGroupSearchIconClick}
                                  aria-label={t('common.search')}
                                  edge="end"
                                  size="small"
                                  disabled={!groupSearchValue.trim()}
                                  className="search-icon-btn"
                                  data-testid="group-search-button"
                                >
                                  <Search
                                    aria-hidden="true"
                                    data-testid="group-search-icon"
                                    className="arr-textfield-icon-color"
                                    fontSize="small"
                                  />
                                </IconButton>
                              )}
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  )}
                </Box>
              )}

              {visibleGroups.length > 0 && (
                <Box className="groups-list" data-testid="groups-list">
                  <Divider className="groups-list-divider" />
                  {visibleGroups.map((g, idx) => (
                    <Box
                      key={g.jobGroupId}
                      className={`group-row ${
                        selectedGroup?.jobGroupId === g.jobGroupId
                          ? 'group-row--selected'
                          : ''
                      } ${
                        isGroupMenuOpen && activeGroupNameForMenu === g.name
                          ? 'group-row--menu-open'
                          : ''
                      } ${
                        isEditingGroup && editingGroupOriginalName === g.name
                          ? 'group-row--editing'
                          : ''
                      }`}
                      data-testid={`group-row-${idx}`}
                    >
                      {isEditingGroup && editingGroupOriginalName === g.name ? (
                        <Box className="group-panel-item group-panel-item--editing">
                          <Box className="item-content">
                            <ArrTextField
                              placeholder={t(
                                'ai.review.leftPanel.enterGroupPlaceholder'
                              )}
                              value={groupName}
                              onChange={handleGroupNameChange}
                              onKeyDown={handleGroupNameKeyDown}
                              onBlur={handleEditBlur}
                              state={hasInlineError ? 'error' : 'enabled'}
                              showErrorIcon={hasInlineError}
                              sizeVariant="small"
                              autoFocus
                              dataTestId="edit-group-name-input"
                            />
                          </Box>
                          {hasInlineError ? (
                            <Typography
                              className="typo-text body4_size group-name-error"
                              data-testid={`review-left-content-panel-error-${idx}`}
                            >
                              {hasSpecialCharError
                                ? t('ai.review.leftPanel.specialCharacterError')
                                : t(
                                    'ai.review.leftPanel.duplicateJobGroupError'
                                  )}
                            </Typography>
                          ) : (
                            <Typography
                              className="typo-text body4_size group-name-counter"
                              data-testid={`review-left-content-panel-of-text-${idx}`}
                            >
                              {groupName.length} of {MAX_GROUP_NAME_LENGTH}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <GroupNameComponent
                          name={g.name}
                          count={g.count}
                          isSelected={
                            selectedGroup?.jobGroupId === g.jobGroupId
                          }
                          dataTestId={`group-item-${idx}`}
                          onClick={() => setSelectedGroup(g)}
                        />
                      )}
                      {!isEditingGroup && (
                        <Box className="group-row-actions">
                          <ArrIconButton
                            icon={<MoreIcon />}
                            ariaLabel={t('ai.review.leftPanel.groupMore')}
                            size="small"
                            shape="none"
                            className={`group-more-button ${
                              isGroupMenuOpen &&
                              activeGroupNameForMenu === g.name
                                ? 'group-more-button--active'
                                : ''
                            }`}
                            testId={`group-more-button-${idx}`}
                            onClick={(event) =>
                              handleGroupMenuClick(event, g.name)
                            }
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              <ArrMenu
                id="group-more-menu"
                ref={groupMenuRef}
                items={groupMenuOptions}
                onItemClick={handleGroupMenuItemClick}
                direction="bottom-left"
                onClose={handleGroupMenuClose}
                className={`group-more-menu ${
                  isGroupMenuOpen && activeGroupNameForMenu ? 'menu-open' : ''
                }`}
              />
            </Box>
          )}
        </>
      ) : (
        <Box className="review-left-panel-collapsed">
          <ArrIconButton
            icon={<ExpandCollapseIcon />}
            ariaLabel={t('common.expand')}
            size="small"
            className="expand-collapse-button-collapsed"
            testId="review-left-panel-expand-collapse"
            onClick={handleCollapseToggle}
          />
        </Box>
      )}
      <ArrModal
        descriptionHasHtml={true}
        showModal={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        showClose={false}
        title={t('ai.review.leftPanel.deleteJobGroupTxt')}
        ariaLabel={t('ai.review.leftPanel.deleteJobGroupTxt')}
        description={t('ai.review.leftPanel.deleteJobGroupDescription', {
          groupName: pendingDeleteGroupName ?? '',
        })}
        ariaDescription={t('ai.review.leftPanel.deleteJobGroupDescription', {
          groupName: pendingDeleteGroupName ?? '',
        })}
        dataTestId="review-left-content-panel"
        buttons={[
          {
            id: 'cancel-delete-group',
            label: t('ai.review.leftPanel.cancelBtn'),
            onClick: handleCloseDeleteModal,
            variant: 'secondary',
            dataTestId: 'cancel-delete-group-button',
          } as ModalButton,
          {
            id: 'confirm-delete-group',
            label: t('ai.review.leftPanel.deleteJobGroupBtn'),
            onClick: handleConfirmDeleteGroup,
            variant: 'danger',
            dataTestId: 'confirm-delete-group-button',
          } as ModalButton,
        ]}
      />
      {showGroupToast && (
        <ArrToastNotification
          message={groupToastMessage}
          status={[groupToastStatus]}
          title={
            groupToastStatus === ToastType.Success
              ? t('common.success')
              : t('common.error')
          }
          onClose={() => setShowGroupToast(false)}
        />
      )}
    </Box>
  );
};

export default ReviewLeftContentPanel;
